import { Group, Stack, Textarea, Title } from "@mantine/core"
import { AbsentApprovalNode } from "../../@client/Absents"
import { Form } from "../../-components/typed-form"
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../utils/form-submission-notifications"
import { useTranslations } from "../../utils/use-t"
import { useInvalidate } from "../../typed-client/hooks/use-invalidate"
import { CancelButton, SubmitButton } from "../../-components/Buttons"
import { DefaultModal } from "../../-components/default-modal"

export function ApproveOrRejectModal({
  approvalNode,
  onApproveClicked,
  onClose,
  leaveRequestId,
}: {
  approvalNode: AbsentApprovalNode | null
  onClose: () => void
  onApproveClicked: (() => void) | undefined
  leaveRequestId: number
}) {
  const ts = useTranslations()
  return (
    <DefaultModal
      opened={approvalNode !== null}
      onClose={onClose}
      title={<Title order={3}>{ts.absences.t("ApproveOrReject")}</Title>}
    >
      {approvalNode !== null ? (
        <RenderCurrentApprovalCard
          leaveRequestId={leaveRequestId}
          approvalNode={approvalNode}
          onClose={onClose}
          onApproveClicked={onApproveClicked}
        />
      ) : null}
    </DefaultModal>
  )
}
function RenderCurrentApprovalCard({
  approvalNode,
  onClose: onClose,
  leaveRequestId,
}: {
  approvalNode: AbsentApprovalNode
  onClose: () => void
  onApproveClicked: (() => void) | undefined
  leaveRequestId: number
}) {
  const invalidate = useInvalidate()

  const ts = useTranslations()

  return (
    <Form<{ comment: string }, "/api/Absence/Command">
      options={{
        onSuccess: () => {
          showSuccessNotification(ts.absences.t("SuccessfullyReviewed"))
          invalidate("/api/Absence/GetApprovalRequestForReview")
          onClose()
        },
        onError: showErrorNotification,
      }}
      transform={(v, e) => {
        const buttonId = (e?.nativeEvent as SubmitEvent)?.submitter?.id
        const decision =
          buttonId === "Approve"
            ? "Approve"
            : buttonId === "Reject"
              ? "Reject"
              : undefined
        if (decision === undefined) {
          throw new Error("invalid submit button")
        }
        return {
          streamId: leaveRequestId,
          command: {
            Case: "ReviewApprovalStep",
            Fields: {
              nodeId: approvalNode.nodeId,
              review: {
                comment: v.comment || null,
                decision: { Case: decision },
              },
            },
          },
        }
      }}
      url="/api/Absence/Command"
    >
      {(f, _, m) => (
        <Stack>
          <Textarea
            pt="xs"
            autoFocus
            placeholder={ts.absences.t("ApprovalCommentPlaceholder")}
            flex={1}
            {...f.getInputProps(_.comment._PATH_)}
          />
          <Group mt="xs" justify="space-between">
            <CancelButton onClick={() => onClose()} />
            <SubmitButton
              id="Reject"
              color="red"
              loading={
                m.isPending &&
                m.variables.command.Case === "ReviewApprovalStep" &&
                m.variables.command.Fields.review.decision.Case === "Reject"
              }
            >
              {ts.appServer.t("Reject")}
            </SubmitButton>
            <SubmitButton
              id="Approve"
              variant="filled"
              color="green"
              loading={
                m.isPending &&
                m.variables.command.Case === "ReviewApprovalStep" &&
                m.variables.command.Fields.review.decision.Case === "Approve"
              }
            >
              {ts.appServer.t("Approve")}
            </SubmitButton>
          </Group>
        </Stack>
      )}
    </Form>
  )
}
